// Cross-Session Pattern Memory
// Quietly observational continuity engine. Inspects archived rehearsals and
// surfaces restrained, forensic observations about recurring conversational
// dynamics. No diagnosis. No certainty. No therapy language.

import { supabase } from "@/integrations/supabase/client";
import { detectArchetypes, ARCHETYPES, type ArchetypeKey } from "@/lib/archetypes";

export interface ContinuityObservation {
  text: string;
  kind: "tactic" | "archetype" | "escalation" | "scenario";
}

interface RawSession {
  id: string;
  created_at: string;
  entry_type: string | null;
  messages: any;
  tactics_sequence: any;
  forensic_baseline: any;
}

const TACTIC_OBSERVATIONS: { match: RegExp; line: string }[] = [
  { match: /invalidat|dismiss/, line: "Recurring invalidation patterns observed across recent exchanges." },
  { match: /minimi[sz]/,         line: "Minimization patterns continue surfacing across rehearsals." },
  { match: /gaslight|reality/,   line: "Reality-distortion framing has reappeared across sessions." },
  { match: /guilt|shame/,        line: "Guilt-laden framing recurs after moments of clarification." },
  { match: /darvo|reversal|blame/, line: "Emotional reversal frequently follows direct accountability." },
  { match: /deflect|pivot/,      line: "Deflection often emerges after direct accountability." },
  { match: /stonewall|withdraw|silence/, line: "Withdrawal patterns repeat following emotional pressure." },
  { match: /triangulat/,         line: "Third-party framing recurs across recent escalations." },
  { match: /control|coerc/,      line: "Subtle control pressure continues appearing in recent rehearsals." },
  { match: /threat|ultimatum/,   line: "Pressure escalations repeat after boundary signals." },
  { match: /boundary/,           line: "Boundary-testing language has surfaced repeatedly." },
];

const ARCHETYPE_LINES: Record<ArchetypeKey, string> = {
  deflector:            "Deflection patterns continue surfacing across recent rehearsals.",
  minimizer:            "Minimization framing recurs across multiple exchanges.",
  reverser:             "Reversal dynamics have emerged repeatedly under pressure.",
  escalator:            "Escalation attempts frequently follow emotional clarification.",
  avoider:              "Avoidance patterns recur during accountability moments.",
  controller:           "Control-shaped framing continues appearing across sessions.",
  emotional_accountant: "Emotional ledger framing recurs across recent rehearsals.",
  interrogator:         "Interrogation cadence has appeared across multiple sessions.",
  future_faker:         "Forward-looking reassurance patterns recur after conflict.",
  boundary_tester:      "Boundary-testing language has surfaced repeatedly.",
};

const SCENARIO_LINES: Record<string, string> = {
  personal:     "Minimization patterns continue appearing in relationship scenarios.",
  professional: "Indirect pressure continues appearing in workplace scenarios.",
};

/** One-line, restrained observation for an individual archived session. */
export function observationForSession(messages: any[], tactics: any[]): string {
  const seq: string[] = (tactics || []).map((t: any) => String(t?.tactic || "").toLowerCase());
  const lateHas = (kw: string) => seq.slice(Math.floor(seq.length / 2)).some((s) => s.includes(kw));

  if (lateHas("darvo") || lateHas("reversal") || lateHas("blame")) return "Escalation increased after clarification attempts.";
  if (lateHas("guilt") || lateHas("shame"))                        return "Emotional minimization persisted across multiple turns.";
  if (lateHas("invalidat") || lateHas("dismiss"))                  return "Invalidation continued after direct statements.";
  if (lateHas("deflect") || lateHas("pivot"))                      return "Deflection intensified following direct statements.";
  if (lateHas("stonewall") || lateHas("withdraw"))                 return "Boundary pressure emerged after disengagement.";
  if (lateHas("control") || lateHas("coerc"))                      return "Subtle pressure continued under emotional clarification.";

  const userMsgs = (messages || []).filter((m) => m?.role === "user");
  const lengths = userMsgs.map((m) => String(m?.content || "").trim().split(/\s+/).filter(Boolean).length);
  if (lengths.length >= 4) {
    const half = Math.floor(lengths.length / 2);
    const early = lengths.slice(0, half).reduce((a, b) => a + b, 0) / Math.max(1, half);
    const late = lengths.slice(half).reduce((a, b) => a + b, 0) / Math.max(1, lengths.length - half);
    if (late > early * 1.4) return "Engagement intensified following emotional pressure.";
    if (late < early * 0.7) return "Responses tightened as pressure escalated.";
  }
  return "Behavioral exchange recorded under sustained pressure.";
}

async function fetchRecentSessions(userId: string, limit = 12): Promise<RawSession[]> {
  const { data, error } = await supabase
    .from("rehearsal_sessions")
    .select("id, created_at, entry_type, messages, tactics_sequence, forensic_baseline")
    .eq("user_id", userId)
    .eq("status", "completed")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error || !data) return [];
  return data as RawSession[];
}

/**
 * Generate a restrained set of cross-session observations.
 * Only returns lines for patterns that recur across 2+ sessions.
 */
export async function generateContinuityObservations(
  userId: string,
  limit = 12
): Promise<ContinuityObservation[]> {
  const sessions = await fetchRecentSessions(userId, limit);
  if (sessions.length < 2) return [];

  const tacticCounts = new Map<string, number>();
  const archetypeCounts = new Map<ArchetypeKey, number>();
  const scenarioCounts = new Map<string, number>();

  for (const row of sessions) {
    const tactics = (row.tactics_sequence as any[]) || [];
    const seenTactic = new Set<string>();
    for (const t of tactics) {
      const tag = String(t?.tactic || "").toLowerCase();
      if (!tag) continue;
      for (const { match } of TACTIC_OBSERVATIONS) {
        if (match.test(tag)) {
          const key = match.source;
          if (!seenTactic.has(key)) {
            seenTactic.add(key);
            tacticCounts.set(key, (tacticCounts.get(key) || 0) + 1);
          }
        }
      }
    }

    const detected = detectArchetypes((row.messages as any[]) || [], tactics);
    const seenArch = new Set<ArchetypeKey>();
    for (const d of detected.slice(0, 2)) {
      if (seenArch.has(d.archetype.key)) continue;
      seenArch.add(d.archetype.key);
      archetypeCounts.set(d.archetype.key, (archetypeCounts.get(d.archetype.key) || 0) + 1);
    }

    if (row.entry_type) {
      scenarioCounts.set(row.entry_type, (scenarioCounts.get(row.entry_type) || 0) + 1);
    }
  }

  const out: ContinuityObservation[] = [];
  const seenLines = new Set<string>();
  const push = (text: string, kind: ContinuityObservation["kind"]) => {
    if (seenLines.has(text)) return;
    seenLines.add(text);
    out.push({ text, kind });
  };

  // Tactics that recurred in 2+ sessions
  for (const [key, count] of tacticCounts) {
    if (count < 2) continue;
    const entry = TACTIC_OBSERVATIONS.find((t) => t.match.source === key);
    if (entry) push(entry.line, "tactic");
  }

  // Archetypes that recurred in 2+ sessions
  for (const [key, count] of archetypeCounts) {
    if (count < 2) continue;
    push(ARCHETYPE_LINES[key], "archetype");
  }

  // Scenario continuity (3+)
  for (const [scn, count] of scenarioCounts) {
    if (count < 3) continue;
    const line = SCENARIO_LINES[scn];
    if (line) push(line, "scenario");
  }

  // Light shuffle so rotation doesn't feel deterministic across reloads
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }

  return out.slice(0, 6);
}

/**
 * For a given session, find restrained "previously observed" continuity lines
 * that surface earlier rehearsals echoing this one. Empty if no continuity.
 */
export async function fetchPreviouslyObservedContinuity(
  userId: string,
  currentSessionId: string,
  currentMessages: any[],
  currentTactics: any[]
): Promise<string[]> {
  const sessions = await fetchRecentSessions(userId, 12);
  const prior = sessions.filter((s) => s.id !== currentSessionId);
  if (prior.length < 1) return [];

  const currentSeq = (currentTactics || []).map((t: any) => String(t?.tactic || "").toLowerCase());
  const currentDetected = detectArchetypes(currentMessages || [], currentTactics);
  const currentArch = new Set(currentDetected.slice(0, 2).map((d) => d.archetype.key));

  const lines: string[] = [];
  const seen = new Set<string>();
  const add = (s: string) => { if (!seen.has(s)) { seen.add(s); lines.push(s); } };

  // Recurring archetype across prior sessions
  const archCounts = new Map<ArchetypeKey, number>();
  for (const row of prior) {
    const detected = detectArchetypes((row.messages as any[]) || [], (row.tactics_sequence as any[]) || []);
    const seenHere = new Set<ArchetypeKey>();
    for (const d of detected.slice(0, 2)) {
      if (seenHere.has(d.archetype.key)) continue;
      seenHere.add(d.archetype.key);
      archCounts.set(d.archetype.key, (archCounts.get(d.archetype.key) || 0) + 1);
    }
  }
  for (const key of currentArch) {
    const count = archCounts.get(key) || 0;
    if (count >= 1) {
      add(`This escalation style resembles prior ${ARCHETYPES[key].name.replace(/^The /, "").toLowerCase()} exchanges.`);
    }
  }

  // Recurring tactic family
  const tacticHits = new Set<string>();
  for (const row of prior) {
    const tactics = (row.tactics_sequence as any[]) || [];
    const tags = tactics.map((t: any) => String(t?.tactic || "").toLowerCase());
    for (const { match, line } of TACTIC_OBSERVATIONS) {
      if (currentSeq.some((c) => match.test(c)) && tags.some((t) => match.test(t))) {
        tacticHits.add(line);
      }
    }
  }
  for (const line of tacticHits) {
    // soften wording for "previously observed" framing
    add(line.replace(/Recurring/i, "Similar").replace(/continue/i, "previously"));
  }

  if (currentSeq.some((s) => /invalidat|minimi[sz]/.test(s)) && prior.length >= 2) {
    add("Repeated conversational pressure observed after emotional clarification.");
  }

  return lines.slice(0, 3);
}
